import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { downloadCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";
import "./AccountingJournalPage.css";

type AccountRow = {
  id: string;
  code: string;
  name: string;
  account_type: string;
  accepts_entries: boolean;
  status: string;
};

type EntryRow = {
  id: string;
  entry_number: string;
  entry_date: string;
  description: string;
  status: string;
  source_type: string | null;
  currency_code: string;
  debit_total: number | string;
  credit_total: number | string;
  line_count: number | string;
  posted_at: string | null;
};

type EntryLine = {
  accountId: string;
  description: string;
  debit: string;
  credit: string;
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  posted: "Posteado",
  reversed: "Revertido",
};

function today() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Lima" }).format(new Date());
}

function monthStart() {
  return today().slice(0, 7) + "-01";
}

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(value || 0));
}

const EMPTY_LINE: EntryLine = {
  accountId: "",
  description: "",
  debit: "",
  credit: "",
};

export function AccountingJournalPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [entries, setEntries] = useState<EntryRow[]>([]);
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [status, setStatus] = useState("");
  const [entryDate, setEntryDate] = useState(today());
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("PEN");
  const [postNow, setPostNow] = useState(true);
  const [lines, setLines] = useState<EntryLine[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    const [accountsResult, entriesResult] = await Promise.all([
      supabase.rpc("list_accounting_accounts_ui", {
        p_organization_id: organizationId,
      }),
      supabase.rpc("list_accounting_entries_ui", {
        p_organization_id: organizationId,
        p_date_from: dateFrom || null,
        p_date_to: dateTo || null,
        p_status: status || null,
      }),
    ]);

    setLoading(false);

    if (accountsResult.error || entriesResult.error) {
      setError(accountsResult.error?.message ?? entriesResult.error?.message ?? "No se pudo cargar Contabilidad General.");
    }

    setAccounts(
      accountsResult.error
        ? []
        : ((accountsResult.data ?? []) as AccountRow[]).filter(
            (account) => account.accepts_entries && account.status === "active",
          ),
    );
    setEntries(entriesResult.error ? [] : ((entriesResult.data ?? []) as EntryRow[]));
  }, [organizationId, dateFrom, dateTo, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setDescription("");
    setCurrency("PEN");
    setPostNow(true);
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setError(null);
    setSuccess(null);
  }, [organizationId]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (acc, line) => ({
          debit: acc.debit + Number(line.debit || 0),
          credit: acc.credit + Number(line.credit || 0),
        }),
        { debit: 0, credit: 0 },
      ),
    [lines],
  );

  function updateLine(index: number, patch: Partial<EntryLine>) {
    setLines((current) =>
      current.map((line, currentIndex) =>
        currentIndex === index ? { ...line, ...patch } : line,
      ),
    );
  }

  async function createEntry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    const payload = lines
      .filter((line) => line.accountId)
      .map((line) => ({
        account_id: line.accountId,
        description: line.description.trim() || null,
        debit: Number(line.debit || 0),
        credit: Number(line.credit || 0),
      }));

    if (payload.length < 2) {
      setError("El asiento necesita al menos dos líneas contables.");
      return;
    }

    if (Math.abs(totals.debit - totals.credit) > 0.005 || totals.debit <= 0) {
      setError("El asiento debe cuadrar: Debe y Haber tienen que ser iguales.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "create_manual_accounting_entry",
      {
        p_organization_id: organizationId,
        p_entry_date: entryDate,
        p_description: description.trim(),
        p_currency_code: currency.toUpperCase(),
        p_lines: payload,
        p_post: postNow,
      },
    );

    setSaving(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setDescription("");
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setSuccess(postNow ? "Asiento creado y posteado." : "Asiento guardado como borrador.");
    await load();
  }

  async function postEntry(entryId: string) {
    setBusyId(entryId);
    setError(null);
    const { error: rpcError } = await supabase.rpc("post_accounting_entry", {
      p_entry_id: entryId,
    });
    setBusyId(null);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setSuccess("Asiento posteado.");
    await load();
  }

  async function reverseEntry(entryId: string) {
    setBusyId(entryId);
    setError(null);
    const { error: rpcError } = await supabase.rpc("reverse_accounting_entry", {
      p_entry_id: entryId,
      p_description: "Reversión desde Contabilidad General",
    });
    setBusyId(null);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setSuccess("Contraasiento generado y original marcado como revertido.");
    await load();
  }

  async function exportJournal() {
    if (!organizationId) return;

    const { data, error: rpcError } = await supabase.rpc(
      "accounting_general_journal",
      {
        p_organization_id: organizationId,
        p_date_from: dateFrom,
        p_date_to: dateTo,
      },
    );

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    downloadCsv(
      `libro-diario-${dateFrom}-${dateTo}.csv`,
      [
        "fecha",
        "asiento",
        "glosa",
        "cuenta",
        "nombre_cuenta",
        "detalle",
        "debe",
        "haber",
        "moneda",
        "origen",
      ],
      ((data ?? []) as Record<string, unknown>[]).map((row) => ({
        fecha: row.entry_date,
        asiento: row.entry_number,
        glosa: row.description,
        cuenta: row.account_code,
        nombre_cuenta: row.account_name,
        detalle: row.line_description,
        debe: row.debit,
        haber: row.credit,
        moneda: row.currency_code,
        origen: row.source_type,
      })),
    );
  }

  if (!selectedOrganization) {
    return <div className="page"><section className="empty-state"><strong>Selecciona una empresa.</strong></section></div>;
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Contabilidad General</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Asientos manuales, posteo, reversión y Libro Diario.</p>
        </div>
        <div className="quotation-actions">
          <button className="ghost-button" type="button" onClick={() => void exportJournal()}>
            Exportar Libro Diario
          </button>
          <Link className="ghost-button" to="/contabilidad">Comprobantes</Link>
          <Link className="ghost-button" to="/reportes">EEFF y reportes</Link>
        </div>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? <div className="form-success">{success}</div> : null}

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Asiento manual</span>
            <h2>Registrar Debe y Haber</h2>
          </div>
          <span className={Math.abs(totals.debit - totals.credit) < 0.005 && totals.debit > 0 ? "status-badge status-badge-approved" : "status-badge"}>
            {Math.abs(totals.debit - totals.credit) < 0.005 && totals.debit > 0 ? "Cuadrado" : "Pendiente de cuadrar"}
          </span>
        </div>

        <form className="accounting-entry-form" onSubmit={createEntry}>
          <label>
            Fecha
            <input type="date" required value={entryDate} onChange={(event) => setEntryDate(event.target.value)} />
          </label>
          <label>
            Moneda
            <input maxLength={3} required value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} />
          </label>
          <label className="accounting-entry-span-2">
            Glosa
            <input required value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Descripción del asiento" />
          </label>

          <div className="accounting-lines accounting-entry-span-2">
            {lines.map((line, index) => (
              <div className="accounting-line" key={index}>
                <label>
                  Cuenta
                  <select required value={line.accountId} onChange={(event) => updateLine(index, { accountId: event.target.value })}>
                    <option value="">Seleccionar</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>{account.code} · {account.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Detalle
                  <input value={line.description} onChange={(event) => updateLine(index, { description: event.target.value })} />
                </label>
                <label>
                  Debe
                  <input type="number" min="0" step="0.01" value={line.debit} onChange={(event) => updateLine(index, { debit: event.target.value, credit: event.target.value ? "" : line.credit })} />
                </label>
                <label>
                  Haber
                  <input type="number" min="0" step="0.01" value={line.credit} onChange={(event) => updateLine(index, { credit: event.target.value, debit: event.target.value ? "" : line.debit })} />
                </label>
                {lines.length > 2 ? (
                  <button className="compact-button compact-button-danger" type="button" onClick={() => setLines((current) => current.filter((_, i) => i !== index))}>
                    Quitar
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          <div className="accounting-entry-totals accounting-entry-span-2">
            <strong>Debe: {money(totals.debit)}</strong>
            <strong>Haber: {money(totals.credit)}</strong>
            <strong>Diferencia: {money(Math.abs(totals.debit - totals.credit))}</strong>
          </div>

          <div className="accounting-entry-actions accounting-entry-span-2">
            <button className="compact-button" type="button" onClick={() => setLines((current) => [...current, { ...EMPTY_LINE }])}>
              Agregar línea
            </button>
            <label className="accounting-entry-check">
              <input type="checkbox" checked={postNow} onChange={(event) => setPostNow(event.target.checked)} />
              Postear inmediatamente
            </label>
            <button className="primary-button" type="submit" disabled={saving}>
              {saving ? "Guardando…" : postNow ? "Guardar y postear" : "Guardar borrador"}
            </button>
          </div>
        </form>
      </section>

      <section className="reports-filters">
        <label>Desde<input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
        <label>Hasta<input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        <label>Estado
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Todos</option>
            <option value="draft">Borrador</option>
            <option value="posted">Posteado</option>
            <option value="reversed">Revertido</option>
          </select>
        </label>
        <button className="ghost-button" type="button" onClick={() => void load()}>Actualizar</button>
      </section>

      <section className="accounting-entry-grid">
        {loading ? <p className="settings-empty">Cargando asientos…</p> : entries.map((entry) => (
          <article className="panel-card accounting-entry-card" key={entry.id}>
            <div className="section-heading">
              <div>
                <span className="status-badge">{STATUS_LABELS[entry.status] ?? entry.status}</span>
                <h3>{entry.entry_number}</h3>
                <p>{entry.entry_date} · {entry.description}</p>
              </div>
              <strong>{money(entry.debit_total)}</strong>
            </div>
            <dl className="customer-meta">
              <div><dt>Origen</dt><dd>{entry.source_type === "manual" ? "Manual" : entry.source_type ?? "—"}</dd></div>
              <div><dt>Líneas</dt><dd>{Number(entry.line_count)}</dd></div>
              <div><dt>Debe</dt><dd>{money(entry.debit_total)}</dd></div>
              <div><dt>Haber</dt><dd>{money(entry.credit_total)}</dd></div>
            </dl>
            <div className="quotation-actions">
              {entry.status === "draft" ? (
                <button className="compact-button" type="button" disabled={busyId === entry.id} onClick={() => void postEntry(entry.id)}>Postear</button>
              ) : null}
              {entry.status === "posted" ? (
                <button className="compact-button compact-button-danger" type="button" disabled={busyId === entry.id} onClick={() => void reverseEntry(entry.id)}>Revertir</button>
              ) : null}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
